import React from 'react';
import { Result, Button } from 'antd';

interface ErrorBoundaryProps {
    children: React.ReactNode;
    /** Fallback UI to display when an error occurs */
    fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * Error Boundary component
 * Catches rendering errors in child components and displays a user-friendly fallback UI.
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <Result
                    status="error"
                    title="页面加载出错"
                    subTitle={
                        this.state.error?.message ||
                        '抱歉，页面渲染过程中发生了错误，请尝试刷新页面。'
                    }
                    extra={[
                        <Button
                            type="primary"
                            key="retry"
                            onClick={this.handleRetry}
                            aria-label="重试加载页面"
                        >
                            重试
                        </Button>,
                        <Button
                            key="home"
                            onClick={() => (window.location.href = '/')}
                            aria-label="返回首页"
                        >
                            返回首页
                        </Button>,
                    ]}
                />
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
